import React from "react";
import { HosDriverProfileDto, Vehicle } from "../../../../api/vehiclesApi";
import DisconnectedPanel from "./DisconnectedPanel";
import ViolationsPanel from "./ViolationsPanel";

const ALERT_PANEL_WIDTH = 340;

interface ViolationsOverlayProps {
  vehicles: Vehicle[];
  hosProfiles: HosDriverProfileDto[];
}

const ViolationsOverlay: React.FC<ViolationsOverlayProps> = React.memo(
  ({ vehicles, hosProfiles }) => {
    const { violatingDrivers, disconnectedDriving } = React.useMemo(() => {
      const violating: Array<{
        vehicle: Vehicle;
        profile: HosDriverProfileDto;
      }> = [];
      const disconnected: Vehicle[] = [];

      vehicles.forEach((vehicle) => {
        const profile = hosProfiles.find((p) => p.vehicleId === vehicle.id);

        // Check for disconnected or OFF_DUTY drivers
        if (!profile || profile.dutyStatus === "OFF_DUTY") {
          // Only add if the vehicle has reported speed recently (within last 5 minutes)
          const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
          const hasRecentUpdate =
            vehicle.lastLocationUpdate &&
            new Date(vehicle.lastLocationUpdate) > fiveMinutesAgo;

          if (hasRecentUpdate && vehicle?.lastSpeed && vehicle.lastSpeed > 0) {
            disconnected.push(vehicle);
          }
          return;
        }

        // Check for HOS violations
        const isViolating =
          profile.driveTime >= 11 * 60 || // 11h limit
          profile.shiftTime >= 14 * 60 || // 14h limit
          profile.cycleTime >= 70 * 60; // 70h limit

        if (isViolating) {
          violating.push({ vehicle, profile });
        }
      });

      return {
        violatingDrivers: violating,
        disconnectedDriving: disconnected,
      };
    }, [vehicles, hosProfiles]);

    if (violatingDrivers.length === 0 && disconnectedDriving.length === 0) {
      return null;
    }

    const hasBothAlertPanels =
      violatingDrivers.length > 0 && disconnectedDriving.length > 0;
    const panelMaxWidth = hasBothAlertPanels
      ? "calc(50vw - 20px)"
      : "calc(100vw - 32px)";

    return (
      <div
        style={{
          position: "absolute",
          bottom: 24,
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "flex-end",
          gap: 8,
          maxWidth: "calc(100vw - 32px)",
          zIndex: 1000,
        }}
      >
        {violatingDrivers.length > 0 && (
          <div
            style={{
              width: ALERT_PANEL_WIDTH,
              maxWidth: panelMaxWidth,
            }}
          >
            <ViolationsPanel violatingDrivers={violatingDrivers} />
          </div>
        )}
        {disconnectedDriving.length > 0 && (
          <div
            style={{
              width: ALERT_PANEL_WIDTH,
              maxWidth: panelMaxWidth,
            }}
          >
            <DisconnectedPanel disconnectedDriving={disconnectedDriving} />
          </div>
        )}
      </div>
    );
  }
);

export default ViolationsOverlay;
